import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SplitDiscountRows from "./SplitDiscountRows.tsx";
import CheckoutTextButton from "../ui/CheckoutTextButton.tsx";
import type { useSplitPayment } from "../hooks/useSplitPayment";
import { getSplitItemKey } from "../hooks/splitItemIdentity.ts";
import { getAddonRows, getBaseMenuItemPrice, getSpiceLevelName } from "../utils/splitCalc.ts";
import { showPrice, getTranslatedKey } from "../../../../../../../utils/commonfunction.js";
import { DISPLAY_METHOD_TAX_OPTIONS } from "../../../../../../../utils/Constants.js";
import { useSelector } from "react-redux";
import type { RestSettingDetails } from "../hooks/types.ts";
import type { OrderItem } from "../hooks/types.ts";
interface RootState {
  restSettings: {
    data: RestSettingDetails;
  };
}

interface SplitByItemsProps {
  splitPayment: ReturnType<typeof useSplitPayment>;
}

const SplitByItems: React.FC<SplitByItemsProps> = ({ splitPayment }) => {
  const { t } = useTranslation();
  const restSettingDetails = useSelector((state: RootState) => state?.restSettings?.data);

  const { splitItemsGroup } = splitPayment;
  const {
    splitItems,
    availableItems,
    handleSplitItemToggle,
    handleSelectAllSplitItems,
    handleDeselectAllSplitItems,
    handleSplitQuantityChange,
  } = splitItemsGroup;

  const remainingListRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const remainingItemCount = availableItems.reduce(
    (count, item) => count + Number(item.quantity || 0),
    0
  );

  const getItemLineTotal = useCallback(
    (item: OrderItem, quantity: number): number => {
      const unitPrice =
        restSettingDetails?.display_method_product_price === DISPLAY_METHOD_TAX_OPTIONS.TAX_EXCLUDED
          ? item?.price
          : item?.total_price;
      return Number(unitPrice || 0) * Number(quantity || 0);
    },
    [restSettingDetails?.display_method_product_price]
  );

  const isTaxExcluded =
    restSettingDetails?.display_method_product_price === DISPLAY_METHOD_TAX_OPTIONS.TAX_EXCLUDED;

  const updateScrollIndicator = useCallback(() => {
    const list = remainingListRef.current;
    if (!list) return;

    const canScroll = list.scrollHeight > list.clientHeight + 4;
    const atBottom = list.scrollHeight - list.scrollTop <= list.clientHeight + 4;

    setShowScrollIndicator(canScroll && !atBottom);
  }, []);

  const handleScrollIndicatorClick = (): void => {
    const list = remainingListRef.current;
    if (!list) return;

    list.scrollBy({
      top: Math.max(list.clientHeight * 0.75, 80),
      behavior: "smooth",
    });
  };

  useEffect(() => {
    updateScrollIndicator();
  }, [availableItems.length, splitItems.length, updateScrollIndicator]);

  const translateValue = getTranslatedKey as (key: string, data?: Record<string, unknown>) => string;

  return (
    <>
      <SplitDiscountRows
        splitPayment={splitPayment}
        onLayoutChange={updateScrollIndicator}
      />
      <div className="split-sidebar-section split-remaining-section">
        <div className="split-remaining-header">
          <h4 className="split-sidebar-label">
            {t("label_remaining_items")}
          </h4>

          <span className="split-remaining-count">
            {remainingItemCount}
          </span>
          <div className="split-bulk-actions">
            <CheckoutTextButton onClick={handleSelectAllSplitItems}>
              {t("label_select_all")}
            </CheckoutTextButton>
            <CheckoutTextButton onClick={handleDeselectAllSplitItems}>
              {t("label_deselect_all")}
            </CheckoutTextButton>
          </div>
        </div>

        <div
          className="split-remaining-list"
          ref={remainingListRef}
          onScroll={updateScrollIndicator}
        >
          {availableItems.length ? (
            availableItems.map((item: OrderItem, index: number) => {
              const itemKey = getSplitItemKey(item);

              const isSplit = splitItems.find(
                (selectedItem: OrderItem) => getSplitItemKey(selectedItem) === itemKey
              );

              const selectedQuantity = isSplit
                ? (isSplit.split_quantity ?? item.quantity)
                : item.quantity;

              const addonRows = getAddonRows(item, translateValue, isTaxExcluded);
              const baseMenuItemPrice = getBaseMenuItemPrice(
                item,
                translateValue,
                isTaxExcluded,
              );
              const spiceLevelName = getSpiceLevelName(item, translateValue);
              return (
                <div
                  className="split-item sidebar-split-item"
                  key={`${itemKey}-${index}`}
                >
                  <div className="split-item-main">
                    <input
                      type="checkbox"
                      className="split-checkbox"
                      checked={!!isSplit}
                      onChange={() => handleSplitItemToggle(item)}
                    />

                    <div className="split-item-details">
                      <div className="split-item-title">
                        <strong className="split-item-name">
                          {translateValue("item_name", item)} x{item.quantity}
                        </strong>
                        <strong className="split-item-base-price">
                          ¥ {showPrice(baseMenuItemPrice)}
                        </strong>
                      </div>
                      {/* Line discriminators, not decoration: two lines of the
                          same menu item can differ only by spice or add-ons, and
                          the cashier is picking which one is being paid for.
                          Not muted, and never truncated — a clipped attribute
                          is as unusable as a missing one. */}
                      {spiceLevelName ? (
                        <small className="split-item-addon">
                          {t("spice_level_text")} -{" "}
                          <strong className="text-capitalize">
                            {spiceLevelName}
                          </strong>
                        </small>
                      ) : null}
                      {addonRows.map((row, addonIndex) => (
                        <small
                          className="split-item-addon text-muted"
                          key={`${itemKey}-addon-${addonIndex}`}
                        >
                          {`+ ${row.label}`}
                          {row.price !== null ? `  ¥ ${showPrice(row.price)}` : ""}
                        </small>
                      ))}
                    </div>
                  </div>

                  <div className="split-item-side">
                    {isSplit && (
                      <div className="qty-box sidebar-qty-box">
                        <button
                          type="button"
                          onClick={() => handleSplitQuantityChange(item, -1)}
                        >
                          -
                        </button>

                        <span>
                          {isSplit.split_quantity ?? 0}
                        </span>

                        <button
                          type="button"
                          onClick={() => handleSplitQuantityChange(item, 1)}
                        >
                          +
                        </button>
                      </div>
                    )}

                    <span className="price">
                      ¥ {showPrice(getItemLineTotal(item, selectedQuantity))}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="split-muted-text">
              {t("label_no_items_left")}
            </p>
          )}
        </div>

        {showScrollIndicator ? (
          <button
            type="button"
            className="split-scroll-indicator"
            onClick={handleScrollIndicatorClick}
            aria-label={t("label_remaining_items")}
          >
            <i className="ti ti-chevron-down"></i>
          </button>
        ) : null}
      </div>
    </>
  );
};

export default SplitByItems;
